import React, { useState, useEffect } from 'react';
import EmailManagementPanel from './EmailManagementPanel';
import HolidayManagementPanel from './HolidayManagementPanel';

const CommunicationAdmin = ({ API, adminToken, isDarkMode, showNotification, onSettingsUpdate }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };
  const authHeadersForForm = { 'Authorization': `Bearer ${adminToken}` };

  // Announcement states
  const [announcements, setAnnouncements] = useState([]);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', bg_color: '#3b82f6', text_color: '#ffffff', notification_type: 'info' });
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showDeleteAnnouncementModal, setShowDeleteAnnouncementModal] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [communicationSubTab, setCommunicationSubTab] = useState('announcements');

  // Load Announcements data helper
  const loadAnnouncementsData = async () => {
    try {
      const announcementsRes = await fetch(`${API}/admin/announcements`, {
        headers: authHeaders
      });
      if (announcementsRes.ok) {
        const announcementsData = await announcementsRes.json();
        setAnnouncements(Array.isArray(announcementsData) ? announcementsData : []);
      } else {
        setAnnouncements([]);
      }
    } catch (error) {
      console.error("Error loading announcements:", error);
    }
  };

  // Load announcements on mount and when switching to announcements sub-tab
  useEffect(() => {
    if (communicationSubTab === 'announcements') {
      loadAnnouncementsData();
    }
  }, [communicationSubTab]);

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', newAnnouncement.title);
      formData.append('content', newAnnouncement.content);
      formData.append('bg_color', newAnnouncement.bg_color);
      formData.append('text_color', newAnnouncement.text_color);
      formData.append('notification_type', newAnnouncement.notification_type || 'info');

      const response = await fetch(`${API}/admin/announcements`, {
        method: 'POST',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification('Announcement created successfully', 'success');
        setNewAnnouncement({ title: '', content: '', bg_color: '#3b82f6', text_color: '#ffffff', notification_type: 'info' });
        setShowAnnouncementModal(false);
        loadAnnouncementsData();
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to create announcement', 'error');
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
      showNotification('Failed to create announcement', 'error');
    }
  };

  const handleUpdateAnnouncement = async (announcementId, updates) => {
    try {
      const formData = new FormData();
      if (updates.title !== undefined) formData.append('title', updates.title);
      if (updates.content !== undefined) formData.append('content', updates.content);
      if (updates.bg_color !== undefined) formData.append('bg_color', updates.bg_color);
      if (updates.text_color !== undefined) formData.append('text_color', updates.text_color);
      if (updates.notification_type !== undefined) formData.append('notification_type', updates.notification_type);
      if (updates.is_active !== undefined) formData.append('is_active', updates.is_active);

      const response = await fetch(`${API}/admin/announcements/${announcementId}`, {
        method: 'PUT',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification('Announcement updated successfully', 'success');
        setEditingAnnouncement(null);
        loadAnnouncementsData();
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to update announcement', 'error');
      }
    } catch (error) {
      console.error('Error updating announcement:', error);
      showNotification('Failed to update announcement', 'error');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    try {
      const response = await fetch(`${API}/admin/announcements/${announcementId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('Announcement deleted successfully', 'success');
        setShowDeleteAnnouncementModal(false);
        setAnnouncementToDelete(null);
        loadAnnouncementsData();
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to delete announcement', 'error');
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      showNotification('Failed to delete announcement', 'error');
    }
  };

  const handleToggleAnnouncementActive = async (announcementId, currentStatus) => {
    await handleUpdateAnnouncement(announcementId, { is_active: !currentStatus });
  };

  return (
    <>
                <div className="space-y-6">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    📢 Communication
                  </h3>

                  {/* Sub-tab navigation */}
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { id: 'announcements', label: 'Announcements', icon: '📢' },
                      { id: 'email', label: 'Email', icon: '📧' },
                      { id: 'holidays', label: 'Holidays', icon: '🎉' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setCommunicationSubTab(tab.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          communicationSubTab === tab.id
                            ? 'bg-blue-600 text-white'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>

                  {communicationSubTab === 'announcements' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      📢 Announcements Management
                    </h3>
                    <button
                      onClick={() => setShowAnnouncementModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      ➕ Create Announcement
                    </button>
                  </div>

                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      💡 <strong>Tip:</strong> Announcements will be displayed on all user dashboards. You can customize colors and toggle visibility.
                    </p>
                  </div>

                  {/* Announcements List */}
                  <div className="space-y-4">
                    {announcements.map(announcement => (
                      <div
                        key={announcement.id}
                        className={`p-6 rounded-lg border-2 ${
                          announcement.is_active
                            ? isDarkMode ? 'border-green-500' : 'border-green-400'
                            : isDarkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}
                        style={{
                          background: announcement.is_active
                            ? announcement.bg_color
                            : isDarkMode ? '#374151' : '#f3f4f6'
                        }}
                      >
                        <div className="mb-4">
                          <div className="w-full">
                            {editingAnnouncement?.id === announcement.id ? (
                              <div className="space-y-3 w-full">
                                {/* Title */}
                                <div>
                                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Title
                                  </label>
                                  <input
                                    type="text"
                                    value={editingAnnouncement.title}
                                    onChange={(e) => setEditingAnnouncement({...editingAnnouncement, title: e.target.value})}
                                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                    placeholder="Title"
                                  />
                                </div>

                                {/* Content */}
                                <div>
                                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Content
                                  </label>
                                  <textarea
                                    value={editingAnnouncement.content}
                                    onChange={(e) => setEditingAnnouncement({...editingAnnouncement, content: e.target.value})}
                                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                    rows="3"
                                    placeholder="Content"
                                  />
                                </div>

                                {/* Notification Type */}
                                <div>
                                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Notification Type
                                  </label>
                                  <select
                                    value={editingAnnouncement.notification_type || 'info'}
                                    onChange={(e) => setEditingAnnouncement({...editingAnnouncement, notification_type: e.target.value})}
                                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                  >
                                    <option value="info">ℹ️ Info - General information</option>
                                    <option value="warning">⚠️ Warning - Important notice</option>
                                    <option value="success">✅ Success - Achievement or completion</option>
                                    <option value="update">🔔 Update - New feature or change</option>
                                    <option value="event">📅 Event - Upcoming activity</option>
                                    <option value="announcement">📢 Announcement - Official statement</option>
                                  </select>
                                </div>

                                {/* Color Pickers */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      Background Color
                                    </label>
                                    <input
                                      type="color"
                                      value={editingAnnouncement.bg_color}
                                      onChange={(e) => setEditingAnnouncement({...editingAnnouncement, bg_color: e.target.value})}
                                      className="w-full h-10 rounded cursor-pointer"
                                    />
                                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {editingAnnouncement.bg_color}
                                    </p>
                                  </div>

                                  <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      Text Color
                                    </label>
                                    <input
                                      type="color"
                                      value={editingAnnouncement.text_color}
                                      onChange={(e) => setEditingAnnouncement({...editingAnnouncement, text_color: e.target.value})}
                                      className="w-full h-10 rounded cursor-pointer"
                                    />
                                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {editingAnnouncement.text_color}
                                    </p>
                                  </div>
                                </div>

                                {/* Color Presets */}
                                <div>
                                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Quick Colors
                                  </label>
                                  <div className="grid grid-cols-4 gap-1">
                                    {[
                                      { bg: '#3b82f6', text: '#ffffff', name: 'Blue' },
                                      { bg: '#10b981', text: '#ffffff', name: 'Green' },
                                      { bg: '#8b5cf6', text: '#ffffff', name: 'Purple' },
                                      { bg: '#ef4444', text: '#ffffff', name: 'Red' },
                                      { bg: '#f59e0b', text: '#000000', name: 'Yellow' },
                                      { bg: '#ec4899', text: '#ffffff', name: 'Pink' },
                                      { bg: '#6366f1', text: '#ffffff', name: 'Indigo' },
                                      { bg: '#14b8a6', text: '#ffffff', name: 'Teal' }
                                    ].map(preset => (
                                      <button
                                        key={preset.name}
                                        type="button"
                                        onClick={() => setEditingAnnouncement({...editingAnnouncement, bg_color: preset.bg, text_color: preset.text})}
                                        className={`h-8 rounded border-2 ${
                                          editingAnnouncement.bg_color === preset.bg ? 'border-blue-500' : 'border-transparent'
                                        }`}
                                        style={{ backgroundColor: preset.bg, color: preset.text }}
                                        title={preset.name}
                                      >
                                        <span className="text-[10px] font-semibold">{preset.name}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* Gradient Presets */}
                                <div>
                                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Gradients
                                  </label>
                                  <div className="grid grid-cols-3 gap-1">
                                    {[
                                      { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#ffffff', name: 'Purple Dream' },
                                      { bg: 'linear-gradient(135deg, #5f72bd 0%, #9b23ea 100%)', text: '#ffffff', name: 'Deep Purple' },
                                      { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', text: '#ffffff', name: 'Ocean Blue' },
                                      { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', text: '#ffffff', name: 'Mint Fresh' },
                                      { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', text: '#ffffff', name: 'Sunset' },
                                      { bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', text: '#ffffff', name: 'Night Ocean' },
                                      { bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', text: '#1f2937', name: 'Soft Pastel' },
                                      { bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', text: '#ffffff', name: 'Pink Blush' },
                                      { bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', text: '#1f2937', name: 'Peach' },
                                      { bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', text: '#1f2937', name: 'Sky Blue' },
                                      { bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)', text: '#ffffff', name: 'Golden Hour' },
                                      { bg: 'linear-gradient(135deg, #667eea 0%, #f093fb 100%)', text: '#ffffff', name: 'Violet' }
                                    ].map(preset => (
                                      <button
                                        key={preset.name}
                                        type="button"
                                        onClick={() => setEditingAnnouncement({...editingAnnouncement, bg_color: preset.bg, text_color: preset.text})}
                                        className={`h-8 rounded border-2 flex items-center justify-center ${
                                          editingAnnouncement.bg_color === preset.bg ? 'border-blue-500' : 'border-transparent'
                                        }`}
                                        style={{ background: preset.bg, color: preset.text }}
                                        title={preset.name}
                                      >
                                        <span className="text-[10px] font-semibold">{preset.name}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* Preview */}
                                <div>
                                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Preview
                                  </label>
                                  <div
                                    className="p-3 rounded-lg"
                                    style={{
                                      background: editingAnnouncement.bg_color,
                                      color: editingAnnouncement.text_color
                                    }}
                                  >
                                    <h5 className="font-semibold mb-1 text-sm">{editingAnnouncement.title || 'Announcement Title'}</h5>
                                    <p className="text-xs">{editingAnnouncement.content || 'Your announcement content will appear here...'}</p>
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-2">
                                  <button
                                    onClick={() => handleUpdateAnnouncement(announcement.id, editingAnnouncement)}
                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                  >
                                    💾 Save Changes
                                  </button>
                                  <button
                                    onClick={() => setEditingAnnouncement(null)}
                                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 font-medium"
                                  >
                                    ✖️ Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <h4 className="text-xl font-bold mb-2" style={{ color: announcement.is_active ? (announcement.text_color || '#ffffff') : isDarkMode ? '#d1d5db' : '#374151' }}>
                                  {announcement.title}
                                </h4>
                                <p style={{ color: announcement.is_active ? (announcement.text_color || '#ffffff') : isDarkMode ? '#9ca3af' : '#4b5563', opacity: announcement.is_active ? 0.9 : 1 }}>
                                  {announcement.content}
                                </p>
                              </>
                            )}
                          </div>
                          {editingAnnouncement?.id !== announcement.id && (
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => handleToggleAnnouncementActive(announcement.id, announcement.is_active)}
                                className={`px-3 py-1 rounded text-sm font-medium ${
                                  announcement.is_active
                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                    : 'bg-gray-500 text-white hover:bg-gray-600'
                                }`}
                                title={announcement.is_active ? 'Active - Click to disable' : 'Inactive - Click to enable'}
                              >
                                {announcement.is_active ? '👁️ Visible' : '👁️‍🗨️ Hidden'}
                              </button>
                            </div>
                          )}
                        </div>

                        {!editingAnnouncement && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingAnnouncement({
                                id: announcement.id,
                                title: announcement.title,
                                content: announcement.content,
                                bg_color: announcement.bg_color,
                                text_color: announcement.text_color,
                                notification_type: announcement.notification_type || 'info'
                              })}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => { setAnnouncementToDelete(announcement); setShowDeleteAnnouncementModal(true); }}
                              className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {announcements.length === 0 && (
                    <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <p className="text-lg mb-2">📢 No announcements yet</p>
                      <p className="text-sm">Create your first announcement to inform all users</p>
                    </div>
                  )}
                </div>
                  )}

                  {communicationSubTab === 'email' && (
                <EmailManagementPanel adminToken={adminToken} />
                  )}

                  {communicationSubTab === 'holidays' && (
                <HolidayManagementPanel adminToken={adminToken} isDarkMode={isDarkMode} onSettingsUpdate={onSettingsUpdate} />
                  )}
                </div>

              {/* Announcement Creation Modal */}
              {showAnnouncementModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                  <div className={`rounded-lg p-6 max-w-lg w-full my-8 max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Create New Announcement
                      </h3>
                      <button
                        onClick={() => {
                          setShowAnnouncementModal(false);
                          setNewAnnouncement({ title: '', content: '', bg_color: '#3b82f6', text_color: '#ffffff', notification_type: 'info' });
                        }}
                        className="text-2xl hover:opacity-70"
                      >
                        ×
                      </button>
                    </div>

                    <form onSubmit={handleCreateAnnouncement} className="space-y-3">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Title
                        </label>
                        <input
                          type="text"
                          value={newAnnouncement.title}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                          className={`w-full px-4 py-2 border rounded-lg ${
                            isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                          placeholder="e.g., Welcome to our platform!"
                          required
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Content
                        </label>
                        <textarea
                          value={newAnnouncement.content}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
                          className={`w-full px-4 py-2 border rounded-lg ${
                            isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                          rows="4"
                          placeholder="Your announcement message..."
                          required
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Notification Type
                        </label>
                        <select
                          value={newAnnouncement.notification_type || 'info'}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, notification_type: e.target.value})}
                          className={`w-full px-4 py-2 border rounded-lg ${
                            isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                        >
                          <option value="info">ℹ️ Info - General information</option>
                          <option value="warning">⚠️ Warning - Important notice</option>
                          <option value="success">✅ Success - Achievement or completion</option>
                          <option value="update">🔔 Update - New feature or change</option>
                          <option value="event">📅 Event - Upcoming activity</option>
                          <option value="announcement">📢 Announcement - Official statement</option>
                        </select>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Choose the type of notification to display the appropriate icon
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Background Color
                          </label>
                          <input
                            type="color"
                            value={newAnnouncement.bg_color}
                            onChange={(e) => setNewAnnouncement({...newAnnouncement, bg_color: e.target.value})}
                            className="w-full h-10 rounded cursor-pointer"
                          />
                          <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {newAnnouncement.bg_color}
                          </p>
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Text Color
                          </label>
                          <input
                            type="color"
                            value={newAnnouncement.text_color}
                            onChange={(e) => setNewAnnouncement({...newAnnouncement, text_color: e.target.value})}
                            className="w-full h-10 rounded cursor-pointer"
                          />
                          <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {newAnnouncement.text_color}
                          </p>
                        </div>
                      </div>

                      {/* Color Presets */}
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Quick Colors
                        </label>
                        <div className="grid grid-cols-4 gap-1">
                          {[
                            { bg: '#3b82f6', text: '#ffffff', name: 'Blue' },
                            { bg: '#10b981', text: '#ffffff', name: 'Green' },
                            { bg: '#8b5cf6', text: '#ffffff', name: 'Purple' },
                            { bg: '#ef4444', text: '#ffffff', name: 'Red' },
                            { bg: '#f59e0b', text: '#000000', name: 'Yellow' },
                            { bg: '#ec4899', text: '#ffffff', name: 'Pink' },
                            { bg: '#6366f1', text: '#ffffff', name: 'Indigo' },
                            { bg: '#14b8a6', text: '#ffffff', name: 'Teal' }
                          ].map(preset => (
                            <button
                              key={preset.name}
                              type="button"
                              onClick={() => setNewAnnouncement({...newAnnouncement, bg_color: preset.bg, text_color: preset.text})}
                              className={`h-8 rounded border-2 ${
                                newAnnouncement.bg_color === preset.bg ? 'border-blue-500' : 'border-transparent'
                              }`}
                              style={{ backgroundColor: preset.bg, color: preset.text }}
                              title={preset.name}
                            >
                              <span className="text-[10px] font-semibold">{preset.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Gradient Presets */}
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Gradients
                        </label>
                        <div className="grid grid-cols-3 gap-1">
                          {[
                            { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#ffffff', name: 'Purple Dream' },
                            { bg: 'linear-gradient(135deg, #5f72bd 0%, #9b23ea 100%)', text: '#ffffff', name: 'Deep Purple' },
                            { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', text: '#ffffff', name: 'Ocean Blue' },
                            { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', text: '#ffffff', name: 'Mint Fresh' },
                            { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', text: '#ffffff', name: 'Sunset' },
                            { bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', text: '#ffffff', name: 'Night Ocean' },
                            { bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', text: '#1f2937', name: 'Soft Pastel' },
                            { bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', text: '#ffffff', name: 'Pink Blush' },
                            { bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', text: '#1f2937', name: 'Peach' },
                            { bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', text: '#1f2937', name: 'Sky Blue' },
                            { bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)', text: '#ffffff', name: 'Golden Hour' },
                            { bg: 'linear-gradient(135deg, #667eea 0%, #f093fb 100%)', text: '#ffffff', name: 'Violet' }
                          ].map(preset => (
                            <button
                              key={preset.name}
                              type="button"
                              onClick={() => setNewAnnouncement({...newAnnouncement, bg_color: preset.bg, text_color: preset.text})}
                              className={`h-8 rounded border-2 flex items-center justify-center ${
                                newAnnouncement.bg_color === preset.bg ? 'border-blue-500' : 'border-transparent'
                              }`}
                              style={{ background: preset.bg, color: preset.text }}
                              title={preset.name}
                            >
                              <span className="text-[10px] font-semibold">{preset.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Preview */}
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Preview
                        </label>
                        <div
                          className="p-3 rounded-lg"
                          style={{
                            background: newAnnouncement.bg_color,
                            color: newAnnouncement.text_color
                          }}
                        >
                          <h5 className="font-semibold mb-1 text-sm">{newAnnouncement.title || 'Announcement Title'}</h5>
                          <p className="text-xs">{newAnnouncement.content || 'Your announcement content will appear here...'}</p>
                        </div>
                      </div>

                      <div className="flex gap-3 pt-2 sticky bottom-0 bg-inherit pb-2">
                        <button
                          type="submit"
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                        >
                          Create Announcement
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAnnouncementModal(false);
                            setNewAnnouncement({ title: '', content: '', bg_color: '#3b82f6', text_color: '#ffffff' });
                          }}
                          className={`px-4 py-2 rounded-lg font-medium ${
                            isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Announcement Delete Confirmation Modal */}
              {showDeleteAnnouncementModal && announcementToDelete && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-70">
                  <div className={`max-w-md w-full rounded-xl shadow-2xl p-6 ${
                    isDarkMode ? 'bg-gray-800 border-2 border-red-700' : 'bg-white border-2 border-red-300'
                  }`}>
                    <div className="text-center">
                      <div className="text-5xl mb-4">🗑️</div>
                      <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Delete Announcement?
                      </h3>
                      <div className="mb-4 p-3 rounded-lg" style={{ background: announcementToDelete.bg_color, color: announcementToDelete.text_color }}>
                        <p className="font-semibold text-sm">{announcementToDelete.title}</p>
                      </div>
                      <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        This announcement will be permanently removed from all user dashboards.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => { setShowDeleteAnnouncementModal(false); setAnnouncementToDelete(null); }}
                          className={`flex-1 py-3 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleDeleteAnnouncement(announcementToDelete.id)}
                          className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
    </>
  );
};

export default CommunicationAdmin;
